<script setup lang="ts">
import {
    EOrderLineType,
    EPaymentTarget,
    EPeriodicity,
    type InterventionItemModel,
} from '@su4r-frontend/api';

const props = defineProps<{
    data: InterventionItemModel;
    priceData?: string;
}>();
const { locale } = useI18n();
const localeRoute = useLocaleRoute();
const { alertSettings, hideAlert, showAlert } = useAlertToast();

const { data: authData, status } = useAuth();

const priceLabel = computed(() => {
    return props.priceData ? props.priceData : props.data.price;
});

const { goToPrescribeFlow } = usePrescribeIntervention();

const orderDTO = reactive<CartOrderModel>({
    culture: locale.value,
    practitioner: {
        emailAddress: authData.value?.user?.email ?? '',
        id: authData.value?.user?.name ?? '',
    },
    orderLines: [
        {
            productReference: props.data.productReference,
            name: props.data.title,
            type: EOrderLineType.INTERVENTION,
        },
    ],
    prescription: {
        supplierInstructions: [],
        duration: {
            amount: 1,
            periodicity: EPeriodicity.MONTHLY,
        },
        instructions: '',
    },
    paymentTargetType: EPaymentTarget.CLINIC,
});

const AMOUNT_OF_LOGOS = 3;
const firstThreeLogos = props.data.certificates.slice(0, AMOUNT_OF_LOGOS);
</script>

<template>
    <article class="tile flex flex-col gap-6 bg-white lg:flex-row lg:flex-wrap">
        <img
            v-if="data.mainImage"
            :src="data.mainImage.url + '?width=704&format=webp'"
            :alt="data.mainImage.alt ?? ''"
            class="w-full object-cover lg:w-65" />
        <div class="flex flex-1 flex-col gap-4">
            <header class="flex flex-col gap-2">
                <div class="flex flex-row items-center justify-between gap-2 lg:gap-6">
                    <h2 class="font-title flex-1 text-xl font-semibold lg:text-2xl">
                        <NuxtLink :to="localeRoute(data.url)">{{ data.title }}</NuxtLink>
                    </h2>
                    <div class="hidden lg:block lg:font-semibold lg:whitespace-nowrap">
                        {{ priceLabel }}
                    </div>
                </div>

                <div
                    class="flex flex-row flex-wrap items-center justify-between gap-2 lg:flex-nowrap">
                    <img
                        v-if="data.vendorLogo"
                        width="92"
                        :alt="data.vendorLogo.alt ?? ''"
                        :src="data.vendorLogo.url + '?width=92&format=webp'"
                        class="max-w-23" />
                    <div class="block font-semibold whitespace-nowrap lg:hidden">
                        {{ data.price }}
                    </div>
                    <div v-if="data.tags && data.tags.length > 0" class="flex flex-wrap gap-2">
                        <span
                            v-for="chip in data.tags"
                            :key="chip.title"
                            class="chip is-disabled not-clickable">
                            {{ chip.title }}
                        </span>
                    </div>
                </div>
            </header>

            <p v-if="data.excerpt" class="line-clamp-3 lg:line-clamp-none">{{ data.excerpt }}</p>
        </div>

        <footer class="flex w-full flex-col justify-between gap-4 lg:flex-row lg:items-center">
            <div class="flex flex-row items-center justify-start gap-4">
                <img
                    v-for="logo in firstThreeLogos"
                    :key="logo.url"
                    class="h-7.5"
                    height="30"
                    :src="logo.url + '?height=30&format=webp'"
                    :alt="logo.alt ?? undefined" />
                <span v-if="data.certificates.length > 3" class="text-brand-black-50">
                    {{
                        $t('common.othersFormat', {
                            amount: data.certificates.length - 3,
                        })
                    }}
                </span>
            </div>

            <div class="flex flex-col items-center gap-2 lg:flex-row lg:gap-4">
                <button
                    v-if="status === 'authenticated' && authData?.user.role === 'practitioner'"
                    class="btn-primary-a"
                    to="#"
                    @click="
                        goToPrescribeFlow(orderDTO, {
                            onError: () =>
                                showAlert(
                                    `Product couldn't be added to the prescription`,
                                    'error',
                                    ANIMATION_DURATION
                                ),
                        })
                    ">
                    {{ $t('interventionOverview.prescribeIntervention') }}
                </button>
                <NuxtLink :to="data.url" class="btn-secondary-a whitespace-nowrap">
                    {{ $t('interventionOverview.viewDetailsButtonTextGeneral') }}
                </NuxtLink>
            </div>
        </footer>
    </article>

    <ClientOnly>
        <Teleport to="body">
            <AlertToast :alert-settings="alertSettings" @hide-alert="hideAlert" />
        </Teleport>
    </ClientOnly>
</template>
